import { cn } from '@/lib/cn'

export interface SpinnerProps {
  className?: string
  size?: 'sm' | 'md' | 'lg'
  /** Optional visible caption under the mark (e.g. "Loading shifts…"). */
  label?: string
  'aria-label'?: string
  'aria-hidden'?: boolean
}

const sizeClasses = {
  sm: 'h-4 w-4',
  md: 'h-8 w-8',
  lg: 'h-12 w-12',
}

/**
 * The product's busy mark: a clock hand sweeping a dial, dragging a trail of
 * tracked time that stretches and contracts behind it. KAAMCO is a time and
 * shifts product, so its wait state reads as time passing rather than a
 * generic ring. Colour follows `currentColor` (brand teal by default), so a
 * `text-*` class on the call site recolours it.
 */
export const Spinner = ({
  className,
  size = 'md',
  label,
  'aria-label': ariaLabel = 'Loading',
  'aria-hidden': ariaHidden,
}: SpinnerProps): React.ReactElement => {
  const mark = (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      className={cn('kc-sweep shrink-0 text-primary dark:text-sky-400', sizeClasses[size], className)}
      role={label || ariaHidden ? undefined : 'status'}
      aria-label={label || ariaHidden ? undefined : ariaLabel}
      aria-hidden={label || ariaHidden ? true : undefined}
      data-loading=""
    >
      {/* Dial */}
      <circle cx="12" cy="12" r="9.5" stroke="currentColor" strokeOpacity="0.16" strokeWidth="2.5" />
      <g className="kc-sweep__hand">
        {/* Trail of elapsed time, drawn counter-clockwise from 12 o'clock so it
            always sits behind the clockwise hand. */}
        <circle
          className="kc-sweep__trail"
          cx="12"
          cy="12"
          r="9.5"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          pathLength="100"
          transform="translate(24 0) scale(-1 1) rotate(-90 12 12)"
        />
        <line x1="12" y1="12" x2="12" y2="5.5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      </g>
      <circle cx="12" cy="12" r="1.9" fill="currentColor" />
    </svg>
  )

  if (!label) return mark

  return (
    <div role="status" className="inline-flex flex-col items-center gap-2.5">
      {mark}
      <span className="kc-sweep-label text-xs font-medium text-gray-500 dark:text-gray-400">{label}</span>
    </div>
  )
}
